// pages/public/ReferralPage.tsx
/**
 * @description Referral registration page
 */
import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Gift,
  Users,
  CheckCircle,
  Home,
  User,
  Mail,
  Phone,
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../../components/layout';
import { Button, Input } from '../../components/ui';
import { useBuilding } from '../../hooks';

export function ReferralPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const buildingSlug = searchParams.get('building');
  const referrerCode = searchParams.get('code');
  const referrerName = searchParams.get('name') || 'A friend';

  const { building } = useBuilding(buildingSlug || '');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // TODO: Actually submit to database
    console.log('Referral submitted:', {
      ...formData,
      referrerCode,
      referrerName,
      buildingId: building?.id
    });

    setIsSubmitting(false);
    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <PublicLayout>
        <div className="p-4 text-center py-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle size={40} className="text-green-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {t('referral.success', { name: referrerName })}
          </h1>

          <p className="text-gray-600 mb-8 max-w-sm mx-auto">
            We've recorded your referral. Our leasing team will be in touch soon!
          </p>

          {buildingSlug && (
            <Link to={`/b/${buildingSlug}`}>
              <Button className="w-full">
                <Home size={18} className="mr-2" />
                View Building
              </Button>
            </Link>
          )}
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="p-4 pb-24">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-purple-100 flex items-center justify-center">
              <Gift size={36} className="text-primary" />
            </div>
            <Sparkles
              size={24}
              className="absolute -top-1 -right-1 text-amber-500"
            />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('referral.title', { name: referrerName })}
          </h1>
          <p className="text-gray-600">{t('referral.subtitle')}</p>
        </div>

        {/* Building info if available */}
        {building && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
              {/* Building image would go here */}
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <Home size={24} />
              </div>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{building.name}</p>
              <p className="text-sm text-gray-500">{building.address}</p>
            </div>
          </div>
        )}

        {/* Benefits */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-8 border border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <Sparkles size={18} className="text-amber-600" />
            {t('referral.benefits')}
          </h3>
          <ul className="space-y-2 text-sm text-amber-800">
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-amber-600" />
              Priority application review
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-amber-600" />
              Move-in bonus when you sign a lease
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={16} className="text-amber-600" />
              Your referrer gets rewarded too!
            </li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <h3 className="font-semibold text-gray-900 mb-2">
            {t('referral.register')}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User size={14} className="inline mr-1" />
              {t('forms.name')} *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              error={errors.name}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail size={14} className="inline mr-1" />
              {t('forms.email')} *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              error={errors.email}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone size={14} className="inline mr-1" />
              {t('forms.phone')} ({t('forms.optional')})
            </label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(416) 555-0123"
            />
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              isLoading={isSubmitting}
              className="w-full"
            >
              <Users size={18} className="mr-2" />
              {t('referral.register')}
            </Button>
          </div>
        </form>

        {/* Back to building */}
        {buildingSlug && (
          <div className="mt-6 text-center">
            <Link
              to={`/b/${buildingSlug}`}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              or continue browsing without registering
            </Link>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
